import React from 'react'
import { MessageSquare, Mail, Phone, Video } from 'lucide-react'
import { Button } from './ui/button'
import { Person } from '../types/profile'
import { cn } from '../lib/utils'

interface PersonCardProps {
  person: Person
  onMessage?: (person: Person) => void
  variant?: 'default' | 'highlighted' | 'compact'
  showContactIcons?: boolean
  className?: string
}

export const PersonCard: React.FC<PersonCardProps> = ({
  person,
  onMessage,
  variant = 'default',
  showContactIcons = false,
  className,
}) => {
  const isHighlighted = variant === 'highlighted'
  const isCompact = variant === 'compact'

  return (
    <div
      className={cn(
        'rounded-lg border bg-card p-4 transition-all',
        isHighlighted && 'border-primary shadow-lg ring-2 ring-primary/20',
        isCompact && 'p-3',
        className
      )}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className={cn(
          'rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0',
          isCompact ? 'h-10 w-10' : 'h-12 w-12'
        )}>
          <span className={cn(
            'text-primary font-medium',
            isCompact ? 'text-sm' : 'text-base'
          )}>
            {person.name
              .split(' ')
              .map(n => n[0])
              .join('')
              .toUpperCase()
              .slice(0, 2)}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h3 className={cn(
                'font-semibold truncate',
                isCompact ? 'text-sm' : 'text-base'
              )}>
                {person.name}
              </h3>
              <p className={cn(
                'text-muted-foreground truncate',
                isCompact ? 'text-xs mt-0.5' : 'text-sm mt-1'
              )}>
                {person.title}
              </p>
              {!isCompact && (
                <p className="text-xs text-muted-foreground mt-1">
                  {person.department}
                </p>
              )}
            </div>
          </div>

          {/* Contact Icons */}
          {showContactIcons && !isCompact && (
            <div className="flex items-center gap-2 mt-3">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onMessage?.(person)}
                aria-label="Message"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label="Email"
              >
                <Mail className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label="Phone"
              >
                <Phone className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label="Video Call"
              >
                <Video className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Message Button for compact/default */}
          {!showContactIcons && (
            <Button
              variant="outline"
              size="sm"
              className="mt-3 w-full"
              onClick={() => onMessage?.(person)}
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Message
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}


